import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  MenuItem,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  createDocumentList,
  updateDocumentList,
  deleteDocumentList,
  buildRegisterDocNumber,
  buildDocumentUid,
  getRegisterDocSource,
  resolveDocumentNameAbbreviation,
  resolveDocumentNameDescription,
  documentAbbreviations,
} from "./docListAPI";
import { secondAxios } from "../../../../../../../api/axios";
import EditableTable from "../../../../../../../components/Tables/EditableTable";
import { useDCS } from "../DCSContext";
import { fetchPlantProjectChoices } from "../../UUIDRegister/uuidRegisterApi";

/** Strip client prefix: "CDT-p001" → "p001", "CDT-p-001" → "p-001" */
function projectNameToNumber(name) {
  const s = String(name ?? "").trim();
  if (!s) return "";
  const dash = s.indexOf("-");
  return dash === -1 ? s : s.slice(dash + 1);
}

/** @param {{ id: string, name: string }[]} projectChoices */
function projectChoicesToNumberOptions(projectChoices) {
  const seen = new Set();
  const options = [];
  for (const p of projectChoices || []) {
    const number = projectNameToNumber(p?.name);
    if (!number || seen.has(number)) continue;
    seen.add(number);
    options.push({
      value: number,
      label: number,
      projectId: String(p?.id ?? "").trim(),
    });
  }
  return options;
}

const abbreviationByCode = new Map(
  documentAbbreviations.map((item) => [
    String(item.abbreviation ?? "").trim(),
    item,
  ]),
);

function findDocumentNameOption(abbr) {
  const code = String(abbr ?? "").trim();
  if (!code) return null;
  const known = abbreviationByCode.get(code);
  if (known) return known;
  return {
    sNo: `legacy-${code}`,
    description: `${code} (current)`,
    abbreviation: code,
  };
}

function buildDocumentNameOptions(currentAbbr) {
  const option = findDocumentNameOption(currentAbbr);
  if (option && !abbreviationByCode.has(currentAbbr)) {
    return [option, ...documentAbbreviations];
  }
  return documentAbbreviations;
}

function readRowSequence(row) {
  const cache = row?._valuesCache ?? {};
  const original = row?.original ?? {};
  const v =
    cache.sequence ??
    cache.sequenceNumber ??
    original.sequence ??
    original.sequenceNumber ??
    original.number ??
    original.seq;
  return v != null && String(v).trim() !== "" ? String(v).trim() : "";
}

function syncUidInCache(row) {
  if (!row._valuesCache) {
    row._valuesCache = {};
  }
  const abbr = resolveDocumentNameAbbreviation(
    row._valuesCache.documentName ??
      row.original?.documentName ??
      row.original?.department ??
      "",
  );
  const seq = readRowSequence(row);
  const uid = buildDocumentUid(abbr, seq);
  row._valuesCache.uid = uid;
  return uid;
}

function DocumentNameAutocompleteEdit({ cell, column, row }) {
  const readAbbr = () =>
    resolveDocumentNameAbbreviation(
      row._valuesCache?.[column.id] ??
        row.original?.documentName ??
        row.original?.department ??
        cell.getValue(),
    );

  const [selected, setSelected] = React.useState(() =>
    findDocumentNameOption(readAbbr()),
  );

  React.useEffect(() => {
    if (!row._valuesCache) {
      row._valuesCache = {};
    }
    const abbr = selected?.abbreviation ?? "";
    row._valuesCache[column.id] = abbr;
    row._valuesCache.documentName = abbr;
    syncUidInCache(row);
  }, [selected, column.id, row]);

  const options = buildDocumentNameOptions(readAbbr());

  return (
    <Autocomplete
      size="small"
      fullWidth
      options={options}
      value={selected}
      onChange={(_event, option) => setSelected(option)}
      getOptionLabel={(option) => option?.description ?? ""}
      isOptionEqualToValue={(a, b) =>
        String(a?.abbreviation ?? "") === String(b?.abbreviation ?? "")
      }
      filterOptions={(opts, { inputValue }) => {
        const q = inputValue.trim().toUpperCase();
        if (!q) return opts;
        return opts.filter(
          (o) =>
            String(o.description ?? "")
              .toUpperCase()
              .includes(q) ||
            String(o.abbreviation ?? "")
              .toUpperCase()
              .includes(q),
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search by description or code…"
          variant="standard"
        />
      )}
      sx={{ minWidth: 280, width: "100%" }}
    />
  );
}

function SequenceEdit({ cell, column, row }) {
  const readSeq = () =>
    String(
      row._valuesCache?.sequence ??
        row._valuesCache?.[column.id] ??
        row.original?.sequence ??
        row.original?.sequenceNumber ??
        row.original?.number ??
        row.original?.seq ??
        cell.getValue() ??
        "",
    ).trim();

  const [value, setValue] = React.useState(readSeq);

  React.useEffect(() => {
    if (!row._valuesCache) {
      row._valuesCache = {};
    }
    row._valuesCache.sequence = value;
    row._valuesCache[column.id] = value;
    syncUidInCache(row);
  }, [value, column.id, row]);

  return (
    <TextField
      size="small"
      fullWidth
      variant="standard"
      type="number"
      inputProps={{ min: 0, step: 1 }}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

function createProjectNumberSelectEdit(projectNumberOptions) {
  return function ProjectNumberSelectEdit({ cell, column, row }) {
    const readNumber = () =>
      String(
        row._valuesCache?.projectNumber ??
          row._valuesCache?.[column.id] ??
          row.original?.projectNumber ??
          cell.getValue() ??
          "",
      ).trim();

    const [value, setValue] = React.useState(readNumber);

    const current = readNumber();
    const inList = new Set(projectNumberOptions.map((o) => o.value));
    const options =
      current && !inList.has(current)
        ? [
            { value: current, label: current, projectId: "" },
            ...projectNumberOptions,
          ]
        : projectNumberOptions;

    const handleChange = (next) => {
      setValue(next);
      if (!row._valuesCache) {
        row._valuesCache = {};
      }
      row._valuesCache.projectNumber = next;
      row._valuesCache[column.id] = next;
      const match = projectNumberOptions.find((o) => o.value === next);
      if (match?.projectId) {
        row._valuesCache.projectId = match.projectId;
      }
    };

    React.useEffect(() => {
      if (!row._valuesCache) {
        row._valuesCache = {};
      }
      row._valuesCache.projectNumber = value;
      row._valuesCache[column.id] = value;
    }, [value, column.id, row]);

    return (
      <TextField
        select
        size="small"
        fullWidth
        variant="standard"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        helperText={
          options.length === 0 ? "No projects available" : undefined
        }
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">
          <em>—</em>
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={`${o.projectId || o.value}`} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    );
  };
}

export const DocLsitInitialData = {
  companyName: "",
  projectNumber: "",
  sequence: "",
  documentName: "",
  uid: "",
  revision: "",
  numberOfSheets: "",
  documentNumber: "",
  description: "",
  projectId: "",
};

const headerCenterSx = {
  "& .Mui-TableHeadCell-Content": {
    justifyContent: "center",
  },
  textAlign: "center",
  fontWeight: 700,
};

export const DocListColumns = (
  _allUsers,
  _type,
  plantMode = false,
  uuidProjectChoices = [],
) => {
  const projectNumberOptions =
    projectChoicesToNumberOptions(uuidProjectChoices);
  const ProjectNumberSelectEdit = createProjectNumberSelectEdit(
    projectNumberOptions,
  );

  return [
    {
      accessorKey: "projectNumber",
      header: "Project Ref",
      size: 16,
      enableEditing: true,
      ...(projectNumberOptions.length > 0
        ? { Edit: ProjectNumberSelectEdit }
        : {}),
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v != null && String(v).trim() !== "" ? String(v) : "—";
      },
    },

    {
      accessorKey: "documentName",
      header: "Document Identifier",
      size: 36,
      enableEditing: true,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      accessorFn: (row) =>
        resolveDocumentNameDescription(
          row.documentName ?? row.department ?? "",
        ),
      Edit: DocumentNameAutocompleteEdit,
      Cell: ({ cell, row }) => {
        const label = resolveDocumentNameDescription(
          cell.getValue() ??
            row.original?.documentName ??
            row.original?.department ??
            "",
        );
        return label !== "" ? label : "—";
      },
    },
    {
      id: "sequence",
      accessorKey: "sequence",
      header: "Sequence #",
      size: 20,
      enableEditing: true,
      Edit: SequenceEdit,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      accessorFn: (row) => {
        const v =
          row.sequence ?? row.sequenceNumber ?? row.number ?? row.seq;
        return v != null && String(v).trim() !== "" ? String(v) : "";
      },
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return v != null && String(v).trim() !== "" ? String(v) : "—";
      },
    },
    {
      accessorKey: "uid",
      header: "UID #",
      size: 26,
      enableEditing: false,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#FFE0B2",
        },
      },
      accessorFn: (row) => {
        const abbr = resolveDocumentNameAbbreviation(
          row.documentName ?? row.department ?? "",
        );
        const seq =
          row.sequence ?? row.sequenceNumber ?? row.number ?? row.seq;
        return (
          buildDocumentUid(abbr, seq) ||
          (row.uid != null ? String(row.uid).trim() : "")
        );
      },
      Cell: ({ row }) => {
        const resolved = getRegisterDocSource(row).uid;
        return resolved !== "" ? resolved : "—";
      },
    },
    {
      id: "documentNumber",
      header: "Document #",
      size: 44,
      enableEditing: false,
      accessorFn: (row) => buildRegisterDocNumber(row),
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#C8E6C9",
        },
      },
      Cell: ({ row }) => {
        const resolved = buildRegisterDocNumber(getRegisterDocSource(row));
        return resolved !== "" ? resolved : "—";
      },
    },
    {
      accessorKey: "revision",
      header: "Latest Revision",
      size: 15,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
    },
    {
      accessorKey: "numberOfSheets",
      header: "No of Sheets",
      size: 24,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
    },
    {
      id: "description",
      accessorFn: (row) => row.description ?? row.title ?? "",
      header: "Description",
      size: 36,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#BBDEFB",
        },
      },
    },
  ];
};

const DocumentList = ({ open, handleClose }) => {
  const {
    allUsers,
    type,
    activeProjectId,
    plant,
    plantId,
    documentList,
    getDocList,
    documentListLoaded,
  } = useDCS();

  const [uuidProjectChoices, setUuidProjectChoices] = useState([]);
  const [plantName, setPlantName] = useState("");

  const documentListRef = useRef(documentList);
  documentListRef.current = documentList;
  const documentListLoadedRef = useRef(documentListLoaded);
  documentListLoadedRef.current = documentListLoaded;

  const defaultPlantProjectIdRef = useRef("");
  const needsNetworkFetchRef = useRef(false);

  const filterDocumentsByType = useCallback(
    (list) => {
      if (!type || type === "all") return list || [];
      return (list || []).filter((doc) => doc.type === type);
    },
    [type],
  );

  useEffect(() => {
    if (!open || !plant || !plantId) {
      setPlantName("");
      return;
    }
    let cancelled = false;
    secondAxios
      .get(`/plant/${plantId}`)
      .then((res) => {
        if (!cancelled) setPlantName(res.data?.name || "");
      })
      .catch(() => {
        if (!cancelled) setPlantName("");
      });
    return () => {
      cancelled = true;
    };
  }, [open, plant, plantId]);

  useEffect(() => {
    if (!open || !plant || !plantId) {
      setUuidProjectChoices([]);
      return undefined;
    }

    let cancelled = false;

    const loadProjects = async () => {
      try {
        const choices = await fetchPlantProjectChoices(plantId);
        if (!cancelled) setUuidProjectChoices(choices);
      } catch (e) {
        console.warn("Document Register project choices load failed:", e);
        if (!cancelled) setUuidProjectChoices([]);
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [open, plant, plantId]);

  const defaultPlantProjectId =
    uuidProjectChoices.length > 0 ? uuidProjectChoices[0].id : "";

  defaultPlantProjectIdRef.current = defaultPlantProjectId;

  const columns = useMemo(
    () =>
      DocListColumns(
        allUsers,
        type,
        Boolean(plant && plantId),
        uuidProjectChoices,
      ),
    [allUsers, type, plant, plantId, uuidProjectChoices],
  );

  const parentId = plant && plantId ? plantId : activeProjectId;

  const getItems = useCallback(async () => {
    if (
      !needsNetworkFetchRef.current &&
      documentListLoadedRef.current &&
      documentListRef.current != null
    ) {
      return filterDocumentsByType(documentListRef.current);
    }

    needsNetworkFetchRef.current = false;
    const list = await getDocList();
    return filterDocumentsByType(list ?? documentListRef.current ?? []);
  }, [filterDocumentsByType, getDocList]);

  const handleCreateItem = useCallback(
    async (createParentId, item) => {
      needsNetworkFetchRef.current = true;

      if (plant && plantId) {
        const selectedProjectId =
          String(item.projectId ?? "").trim() ||
          defaultPlantProjectIdRef.current;
        if (!selectedProjectId) {
          throw new Error(
            "No project available for this document register row.",
          );
        }
        const { projectId, projectName, companyName, ...docData } = item;
        return createDocumentList(type, selectedProjectId, docData);
      }
      return createDocumentList(type, createParentId, item);
    },
    [plant, plantId, type],
  );

  const handleUpdateItem = useCallback(async (updateDocument) => {
    needsNetworkFetchRef.current = true;
    return updateDocumentList(updateDocument);
  }, []);

  const handleDeleteItem = useCallback(async (id) => {
    needsNetworkFetchRef.current = true;
    return deleteDocumentList(id);
  }, []);

  const tableApi = useMemo(
    () => ({
      getItems,
      createItem: handleCreateItem,
      updateItem: handleUpdateItem,
      deleteItem: handleDeleteItem,
    }),
    [getItems, handleCreateItem, handleUpdateItem, handleDeleteItem],
  );

  const initialData = useMemo(
    () =>
      defaultPlantProjectId
        ? { ...DocLsitInitialData, projectId: defaultPlantProjectId }
        : DocLsitInitialData,
    [defaultPlantProjectId],
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "96vw", md: "min(1280px, 96vw)" },
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          Document Register
        </Typography>
        {plant && plantName ? (
          <Typography variant="h4" color="darkBlue" sx={{ pb: 2 }}>
            {plantName}
          </Typography>
        ) : null}
        {open ? (
          <EditableTable
            name="Doc Register"
            displayMode="row"
            columns={columns}
            enableEditing={true}
            enableCreate={true}
            initialData={initialData}
            api={tableApi}
            parentId={parentId}
          />
        ) : null}
      </Box>
    </Modal>
  );
};

export default DocumentList;
